import { useCallback, useMemo } from 'react';
import { usePermissions, MENU_PERMISSIONS, SCREEN_PERMISSIONS, FEATURE_PERMISSIONS, FEATURE_FLAGS, HIERARCHY_LEVELS } from './PermissionContext';

/**
 * Custom hook for checking permissions in components
 * Provides convenient methods for common permission checks
 */
export const usePermission = () => {
  const {
    permissions,
    features,
    dataScope,
    loading,
    error,
    userRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isFeatureEnabled,
    hasMinimumRole,
    getPermissionScope,
    canAccessMenu,
    refreshPermissions,
  } = usePermissions();

  // Check if user can view a specific screen
  const canViewScreen = useCallback((screenName) => {
    const screenCode = `SCREEN_${screenName.toUpperCase()}_VIEW`;
    return hasPermission(screenCode);
  }, [hasPermission]);

  // Check if user can manage a specific resource
  const canManage = useCallback((resourceName) => {
    const manageCode = `SCREEN_${resourceName.toUpperCase()}_MANAGE`;
    return hasPermission(manageCode);
  }, [hasPermission]);

  // Check if user can perform a specific action
  const canPerformAction = useCallback((action, resource) => {
    const actionCode = `FEATURE_${resource.toUpperCase()}_${action.toUpperCase()}`;
    return hasPermission(actionCode);
  }, [hasPermission]);

  // Check if user is at least a certain role level
  const isAtLeast = useCallback((role) => {
    return hasMinimumRole(role);
  }, [hasMinimumRole]);

  // Check role shortcuts
  const isSuperAdmin = useMemo(() => userRole === 'SUPER_ADMIN', [userRole]);
  const isOrgAdmin = useMemo(() => userRole === 'ADMIN' || isSuperAdmin, [userRole, isSuperAdmin]);
  const isLocationManager = useMemo(() => userRole === 'MANAGER' || isOrgAdmin, [userRole, isOrgAdmin]);
  const isEmployee = useMemo(() => userRole === 'MEMBER' || isLocationManager, [userRole, isLocationManager]);

  // Get all menu items the user can access
  const accessibleMenus = useMemo(() => {
    if (userRole === 'SUPER_ADMIN') {
      return Object.keys(MENU_PERMISSIONS);
    }
    return permissions
      .filter(p => p.permission_type === 'MENU')
      .map(p => p.permission_code.replace('MENU_', ''));
  }, [permissions, userRole]);

  // Get all enabled features for the organization
  const enabledFeatures = useMemo(() => {
    return features
      .filter(f => f.is_enabled)
      .map(f => f.feature_code);
  }, [features]);

  // Check data scope for filtering
  const canAccessAllOrgData = useMemo(() => {
    return dataScope === 'GLOBAL' || dataScope === 'ORG';
  }, [dataScope]);

  const canAccessLocationData = useMemo(() => {
    return dataScope !== 'SELF';
  }, [dataScope]);

  // Permission check with feature flag combination
  const canUseFeature = useCallback((featureCode) => {
    // First check if the feature is enabled for the org
    const featureEnabled = isFeatureEnabled(featureCode);
    if (!featureEnabled) return false;

    // Then check if user has permission to use it
    const permissionCode = `FEATURE_${featureCode.replace('FEATURE_', '')}`;
    return hasPermission(permissionCode);
  }, [isFeatureEnabled, hasPermission]);

  return {
    // State
    loading,
    error,
    userRole,
    dataScope,
    permissions,
    features,

    // Basic checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isFeatureEnabled,
    hasMinimumRole,
    getPermissionScope,
    canAccessMenu,

    // Convenience methods
    canViewScreen,
    canManage,
    canPerformAction,
    isAtLeast,
    canUseFeature,

    // Role shortcuts
    isSuperAdmin,
    isOrgAdmin,
    isLocationManager,
    isEmployee,

    // Computed values
    accessibleMenus,
    enabledFeatures,
    canAccessAllOrgData,
    canAccessLocationData,

    // Actions
    refreshPermissions,

    // Constants for reference
    MENU_PERMISSIONS,
    SCREEN_PERMISSIONS,
    FEATURE_PERMISSIONS,
    FEATURE_FLAGS,
    HIERARCHY_LEVELS,
  };
};

/**
 * Hook for checking a single permission
 * @param {string} permissionCode - The permission code to check
 * @returns {boolean} - Whether the user has the permission
 */
export const useHasPermission = (permissionCode) => {
  const { hasPermission, loading } = usePermissions();

  const allowed = useMemo(() => {
    if (loading) return false;
    return hasPermission(permissionCode);
  }, [hasPermission, permissionCode, loading]);

  return { allowed, loading };
};

/**
 * Hook for checking if a feature is enabled
 * @param {string} featureCode - The feature code to check
 * @returns {boolean} - Whether the feature is enabled
 */
export const useFeatureEnabled = (featureCode) => {
  const { isFeatureEnabled, loading } = usePermissions();

  const enabled = useMemo(() => {
    if (loading) return false;
    return isFeatureEnabled(featureCode);
  }, [isFeatureEnabled, featureCode, loading]);

  return { enabled, loading };
};

/**
 * Hook for checking role level
 * @param {string} requiredRole - The minimum required role
 * @returns {boolean} - Whether the user has the required role level
 */
export const useHasRole = (requiredRole) => {
  const { hasMinimumRole, loading } = usePermissions();

  const hasRole = useMemo(() => {
    if (loading) return false;
    return hasMinimumRole(requiredRole);
  }, [hasMinimumRole, requiredRole, loading]);

  return { hasRole, loading };
};

export default usePermission;
